import React, { createContext, useState, useContext, useEffect } from 'react';
import { fetchCounters, createCounter, deleteCounter, updateCounter } from '../services/api';
import { Counter } from '../types';
import { useUser } from './UserContext';

/**
 * Type definition for the Counter Context
 * Contains all counter-related state and functions
 */
export type CounterContextType = {
  counters: Counter[];
  loading: boolean;
  error: string | null;
  selectedCounterId: string | null;
  addCounter: (name: string, isPrivate?: boolean, email?: string) => Promise<Counter>;
  removeCounter: (id: string, isOwnerUrl?: boolean) => Promise<void>;
  updateCounterDetails: (
    id: string, 
    name: string, 
    isPrivate?: boolean, 
    timeLimitType?: 'deadline' | 'countdown' | null,
    deadline?: string | null,
    timezone?: string | null,
    countdownMinutes?: number | null,
    countdownStartedAt?: string | null
  ) => Promise<Counter>;
  selectCounter: (id: string | null) => void;
  refreshCounters: () => Promise<void>;
  findCounterByOwnerUrl: (ownerUrl: string) => Counter | undefined;
  hasAccessToCounter: (counter: Counter, isOwnerUrl?: boolean) => boolean;
};

const CounterContext = createContext<CounterContextType | undefined>(undefined);

/**
 * Provider component for counter management
 * Provides state and functions to work with counters
 * 
 * @param children - Child components that will have access to the counter context
 */
export const CounterProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [counters, setCounters] = useState<Counter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCounterId, setSelectedCounterId] = useState<string | null>(null);
  const { user, isAdmin } = useUser();
  const userEmail = user.email;

  /**
   * Fetches counters from the API and updates state
   * Sorts counters by creation date (newest first)
   */
  const refreshCounters = async () => {
    try {
      setLoading(true);
      const fetchedCounters = await fetchCounters();
      
      // Sort counters by createdAt date in descending order (newest first)
      const sortedCounters = [...fetchedCounters].sort((a, b) => {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });
      
      setCounters(sortedCounters);
      
      // If no counter is selected and we have counters, select the first one
      if (!selectedCounterId && sortedCounters.length > 0) {
        setSelectedCounterId(sortedCounters[0]._id);
      }
      
      setError(null);
    } catch (err) {
      setError('Failed to load counters');
      console.error('Error fetching counters:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshCounters();
  }, []);

  /**
   * Creates a new counter
   * 
   * @param name - The name of the counter
   * @param isPrivate - Whether the counter should be private
   * @param email - Optional email for notifications
   * @returns The newly created counter
   */
  const addCounter = async (name: string, isPrivate?: boolean, email?: string) => {
    try {
      // If user is not logged in, mark counter as public so it can be accessed via owner URL
      // Otherwise, associate the counter with the user's email
      const newCounter = await createCounter({ 
        name, 
        isPrivate, 
        email,
        ownerEmail: userEmail || undefined,
        isPublic: !userEmail // Set isPublic true when no user is logged in
      });
      await refreshCounters();
      return newCounter;
    } catch (err) {
      setError('Failed to create counter');
      console.error('Error creating counter:', err);
      throw err;
    }
  };

  /**
   * Checks if the current user has access to view a counter
   * Access rules from first principles:
   * 1. Admin can access all counters (requires password in settings)
   * 2. Counter owners can access their own counters
   * 3. Public counters are accessible to everyone
   * 4. Non-owners cannot access via owner URLs
   * 
   * @param counter - The counter to check access for
   * @param isOwnerUrl - Whether the counter is being accessed via owner URL
   * @returns Boolean indicating if user has access
   */
  const hasAccessToCounter = (counter: Counter, isOwnerUrl: boolean = false): boolean => {
    // Admin users have access to all counters
    if (isAdmin) {
      return true;
    }
    
    // If counter is marked as public, always grant access for normal viewing
    if (counter.isPublic) {
      return true;
    }
    
    // If the user is the counter owner, they have access
    if (counter.ownerEmail === userEmail) {
      return true;
    }
    
    // If accessing via owner URL but user is not the owner, deny access
    if (isOwnerUrl && counter.ownerEmail !== userEmail) {
      return false;
    }
    
    // If the counter has no owner, allow public access via regular ID
    if (!counter.ownerEmail && !isOwnerUrl) {
      return true;
    }
    
    return false;
  };

  /**
   * Deletes a counter by ID
   * 
   * @param id - The ID of the counter to delete
   * @param isOwnerUrl - Whether the ID is an owner URL instead of a regular ID
   */
  const removeCounter = async (id: string, isOwnerUrl = false) => {
    try {
      await deleteCounter(id, isOwnerUrl);
      
      // If the deleted counter was selected, select the first available counter
      if (selectedCounterId === id) {
        const remainingCounters = counters.filter(counter => counter._id !== id);
        setSelectedCounterId(remainingCounters.length > 0 ? remainingCounters[0]._id : null);
      }
      
      await refreshCounters();
    } catch (err) {
      setError('Failed to delete counter');
      console.error('Error deleting counter:', err);
      throw err;
    }
  };

  /**
   * Updates an existing counter with new details
   * 
   * @param id - The ID of the counter to update
   * @param name - The new name for the counter
   * @param isPrivate - Whether the counter should be private
   * @param timeLimitType - Type of time limit (deadline, countdown, or null for no limit)
   * @param deadline - The deadline date and time (for deadline type)
   * @param timezone - The timezone for the deadline
   * @param countdownMinutes - The countdown duration in minutes (for countdown type)
   * @param countdownStartedAt - When the countdown started (for countdown type)
   * @returns The updated counter
   */
  const updateCounterDetails = async (
    id: string, 
    name: string, 
    isPrivate?: boolean,
    timeLimitType?: 'deadline' | 'countdown' | null,
    deadline?: string | null,
    timezone?: string | null,
    countdownMinutes?: number | null,
    countdownStartedAt?: string | null
  ) => {
    try {
      const updatedCounter = await updateCounter(id, { 
        name, 
        isPrivate,
        timeLimitType,
        deadline,
        timezone,
        countdownMinutes,
        countdownStartedAt
      });
      await refreshCounters();
      return updatedCounter;
    } catch (err) {
      setError('Failed to update counter');
      console.error('Error updating counter:', err);
      throw err;
    }
  };

  /**
   * Sets the selected counter ID
   * 
   * @param id - The ID of the counter to select or null to deselect
   */
  const selectCounter = (id: string | null) => {
    setSelectedCounterId(id);
  };

  /**
   * Finds a counter by its owner URL
   * 
   * @param ownerUrl - The owner URL to search for
   * @returns The matching counter or undefined if not found
   */
  const findCounterByOwnerUrl = (ownerUrl: string): Counter | undefined => {
    return counters.find(counter => counter.ownerUrl === ownerUrl);
  };

  return (
    <CounterContext.Provider
      value={{
        counters,
        loading,
        error,
        selectedCounterId,
        addCounter,
        removeCounter,
        updateCounterDetails,
        selectCounter,
        refreshCounters,
        findCounterByOwnerUrl,
        hasAccessToCounter
      }}
    >
      {children}
    </CounterContext.Provider>
  );
};

/**
 * Hook to use the counter context in components
 * @throws Error if used outside of CounterProvider
 * @returns The counter context
 */
export const useCounters = () => {
  const context = useContext(CounterContext);
  if (context === undefined) {
    throw new Error('useCounters must be used within a CounterProvider');
  }
  return context;
}; 